import { useContext, useEffect, useRef, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import Canceling from "../../assets/img/closeIcon.svg";
import TenantContext from "../../context/tenant/TenantContext";
import AuthContext from "../../context/auth/AuthContext";
import { haveValue, toast } from "../../helper/common";

const TenantKelify = () => {
  const { user, loadUser } = useContext(AuthContext);
  const { shareProfileFromKelify, loadSharedProfiles } =
    useContext(TenantContext);
  const [url, setUrl] = useState("");
  const { _t } = useTranslator();

  const shareUserProfile = async (payload) => {
    try {
      const res = await shareProfileFromKelify(payload);
      if (res?.success) {
        toast.success(res?.message);
        const shareLink = document.getElementById("shareProfileLink");
        if (shareLink) {
          await loadSharedProfiles(user?._id, "full");
          shareLink.click();
        }
      }
    } catch (error) {
      console.log("Error down", error);
      toast.error(_t("failedToShareProfile"));
    }
  };

  useEffect(() => {
    if (haveValue(user)) {
      const params = new URLSearchParams(window.location.search);
      const source_url = params.get("source_url");
      const agency_id = params.get("agency_id");

      if (source_url && agency_id) {
        try {
          const decodedUrl = atob(source_url);
          const payload = {
            kelify_agency_id: agency_id,
            source_url: decodedUrl,
          };
          shareUserProfile(payload);
        } catch (error) {
          console.error("Invalid Base64 string", error);
        }
      }

      // Remove query parameters from the URL
      params.delete("source_url");
      params.delete("agency_id");

      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : "");

      // Update the URL in the browser without reload
      window.history.replaceState({}, document.title, newUrl);
      const kelifyId = user?.referredId?.agency_id?.kelify_id;

      let iframeUrl = "https://kelify.com/embed/search";
      if (kelifyId) {
        iframeUrl += `?agency_id=${kelifyId}`;
      }

      setUrl(iframeUrl);
    } else {
      loadUser("tenant");
    }
  }, [user]);

  // ==== STATES ====
  const [searchVal, setSearchVal] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);

  const serviceRef = useRef(null);

  const MIN_RANGE = 0;
  const MAX_RANGE = 5000;
  const GAP = 1;
  const [min, setMin] = useState(null);
  const [max, setMax] = useState(null);
  const [tempMin, setTempMin] = useState(MIN_RANGE);
  const [tempMax, setTempMax] = useState(MAX_RANGE);
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);

  const [selectedBHK, setSelectedBHK] = useState([]);
  const [bathroom, setBathroom] = useState([]);

  const bhkOptions = ["0", "1", "2", "3", "4"];
  const bathroomOptions = ["1", "2", "3"];

  // ==== HANDLERS ====
  const toggleBHK = (option) => {
    setSelectedBHK((prev) =>
      prev.includes(option)
        ? prev.filter((b) => b !== option)
        : [...prev, option]
    );
  };

  const handleSetBudget = () => {
    // If full range selected (not customized), reset selection
    if (tempMin === MIN_RANGE && tempMax === MAX_RANGE) {
      setMin(null);
      setMax(null);
    } else {
      setMin(tempMin);
      setMax(tempMax);
    }
  };

  const getBudgetText = () => {
    if (min === null || max === null) return _t("selectRange");
    return `${min}€ - ${max}€`;
  };

  // ==== UPDATE IFRAME URL ====
  useEffect(() => {
    if (url) {
      const queryParams = new URLSearchParams();

      // Price filters
      if (min !== null) queryParams.set("min_price", min);
      if (max !== null) queryParams.set("max_price", max);
      if (lat !== null) queryParams.set("lat", lat);
      if (lng !== null) queryParams.set("lng", lng);

      // BHK filters
      if (selectedBHK.length > 0) {
        selectedBHK.forEach((bhk) => {
          queryParams.append("rooms[]", bhk);
        });
      }

      // Bathroom filters
      if (bathroom.length > 0) {
        bathroom.forEach((b) => {
          queryParams.append("bathrooms[]", b);
        });
      }

      const newUrl = `https://kelify.com/embed/search?${queryParams.toString()}`;
      setUrl(newUrl);
    }
  }, [min, max, selectedBHK, bathroom, lng, lat]);

  useEffect(() => {
    if (!window.google || !searchVal || isSelecting) {
      setLng(null);
      setLat(null);
      return setSuggestions([]);
    }

    if (!serviceRef.current) {
      serviceRef.current = new window.google.maps.places.AutocompleteService();
    }

    serviceRef.current.getPlacePredictions(
      {
        input: searchVal,
        componentRestrictions: { country: "es" },
      },
      (predictions, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setSuggestions(predictions);
        } else {
          setSuggestions([]);
        }
      }
    );
  }, [searchVal]);

  const handleSelect = (description) => {
    setIsSelecting(true);
    setSearchVal(description);
    setSuggestions([]);
    setLng(null);
    setLat(null);

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: description }, (results, status) => {
      if (status === "OK" && results[0]) {
        const { lat, lng } = results[0].geometry.location;
        setLng(lng());
        setLat(lat());
      } else {
        console.error("Geocode failed:", status);
      }
    });

    setTimeout(() => setIsSelecting(false), 100);
  };

  return (
    <div className="main-page-wrapper mb-4 position-relative">
      <header className="kelify-header">
        {/* LEFT SIDE */}
        <div className="kelify-header-left">
          <span className="kelify-collab-text">
            {_t("inCollaborationWith")}
          </span>
          <img
            src="/images/kelify-logo.png"
            className="kelify-collab-logo"
            alt="Kelify Logo"
          />
        </div>

        {/* SEARCH BAR */}
        <div className="kelify-search-bar">
          <img
            src="/images/search-icon-grey.svg"
            alt=""
            className="searchIcon"
          />
          {(searchVal || suggestions.length > 0) && (
            <img
              src={Canceling}
              className="input-search-close-btn"
              onClick={() => {
                setSearchVal("");
                setSuggestions([]); // optional if you want to clear dropdown too
              }}
            />
          )}
          <input
            type="text"
            className="kelify-input"
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            placeholder={_t("searchProperty")}
          />
          {suggestions.length > 0 && (
            <ul className="autocomplete-dropdown">
              <h1>{_t("results")}</h1>
              {suggestions.map((item) => (
                <li
                  className="cursor-pointer"
                  key={item.place_id}
                  onClick={() => handleSelect(item.description)}
                >
                  {item.description}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* RIGHT SIDE FILTERS */}
        <div className="kelify-header-right">
          {/* ==== BUDGET FILTER ==== */}
          <div className="dropdown kelify-dropdown">
            <button
              className="btn btn-light dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {getBudgetText()}
              <img src="/images/icon-down-dark.svg" alt="" />
            </button>
            <ul className="dropdown-menu setbudget-menu">
              <div className="setbudget-slider-wrapper">
                <input
                  type="range"
                  min={MIN_RANGE}
                  max={MAX_RANGE}
                  value={tempMin}
                  onChange={(e) => {
                    const value = Math.min(
                      Number(e.target.value),
                      tempMax - GAP
                    );
                    setTempMin(value);
                  }}
                  className="setbudget-slider"
                />
                <input
                  type="range"
                  min={MIN_RANGE}
                  max={MAX_RANGE}
                  value={tempMax}
                  onChange={(e) => {
                    const value = Math.max(
                      Number(e.target.value),
                      tempMin + GAP
                    );
                    setTempMax(value);
                  }}
                  className="setbudget-slider"
                />

                {/* Filled Track */}
                <div
                  className="setbudget-slider-track"
                  style={{
                    left: `${(tempMin / MAX_RANGE) * 100}%`,
                    right: `${100 - (tempMax / MAX_RANGE) * 100}%`,
                  }}
                ></div>
              </div>

              {/* Inputs */}
              <div className="set-budget-content">
                <input
                  type="number"
                  value={tempMin}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setTempMin(Math.min(value, tempMax - GAP));
                  }}
                  className="setbudget-input"
                />
                <span>{_t("to")}</span>
                <input
                  type="number"
                  value={tempMax}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setTempMax(Math.max(value, tempMin + GAP));
                  }}
                  className="setbudget-input"
                />
                <button className="setbudget-btn" onClick={handleSetBudget}>
                  {_t("set")}
                </button>
              </div>
            </ul>
          </div>

          {/* ==== BHK TYPE ==== */}
          <div className="dropdown kelify-dropdown">
            <button
              className="btn btn-light dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {_t("bhkType")}
              <img src="/images/icon-down-dark.svg" alt="" />
            </button>
            <ul
              className="dropdown-menu p-0"
              style={{ minWidth: "fit-content", minHeight: "fit-content" }}
            >
              <div className="bhk-dropdown">
                {bhkOptions.map((option, index) => (
                  <li
                    key={index}
                    className={
                      selectedBHK.includes(option) ? "kelify-li-active" : ""
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleBHK(option);
                    }}
                  >
                    {option} {_t("bhk")}
                  </li>
                ))}
              </div>
            </ul>
          </div>

          {/* ==== BATHROOMS ==== */}
          <div className="dropdown kelify-dropdown">
            <button
              className="btn btn-light dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {_t("bathrooms")}
              <img src="/images/icon-down-dark.svg" alt="" />
            </button>
            <ul
              className="dropdown-menu p-0"
              style={{ minWidth: "fit-content" }}
            >
              <div className="bhk-dropdown">
                {bathroomOptions.map((opt, i) => (
                  <li
                    key={i}
                    className={bathroom.includes(opt) ? "kelify-li-active" : ""}
                    onClick={(e) => {
                      e.stopPropagation();
                      setBathroom((prev) =>
                        prev.includes(opt)
                          ? prev.filter((b) => b !== opt)
                          : [...prev, opt]
                      );
                    }}
                  >
                    {opt} {_t("bathroom")}
                  </li>
                ))}
              </div>
            </ul>
          </div>
        </div>
      </header>

      {/* ==== IFRAME ==== */}
      <div
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {url ? (
          <iframe
            src={url}
            title="Embedded Page"
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        ) : (
          <p>Loading...</p>
        )}
      </div>
    </div>
  );
};

export default TenantKelify;
